import React, { useState, useEffect } from 'react';
import './AgencyDashboard.css';
import api from '../services/api';
import * as XLSX from 'xlsx';

const AgencyDashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({
    totalSchools: 0,
    activeStudents: 0,
    activeDevices: 0,
    totalRevenue: 0,
    commissionEarned: 0
  });
  const [schools, setSchools] = useState([]);
  const [reports, setReports] = useState([]);
  const [devices, setDevices] = useState([]);
  const [showAddSchool, setShowAddSchool] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showSingleAssign, setShowSingleAssign] = useState(false);
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: ''
  });
  const [newSchool, setNewSchool] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    commission_type: 'percentage',
    sales_commission: process.env.REACT_APP_SCHOOL_DEFAULT_COMMISSION || 10
  });
  const [singleAssignForm, setSingleAssignForm] = useState({
    device_id: '',
    school_id: ''
  });
  const [bulkAssignFile, setBulkAssignFile] = useState(null);
  const [bulkAssignResult, setBulkAssignResult] = useState(null);
  const [commissionSubTab, setCommissionSubTab] = useState('summary');
  const [commissionSummary, setCommissionSummary] = useState({
    total_accrued: 0,
    total_pending: 0,
    total_paid: 0,
    transaction_count: 0
  });
  const [commissionReports, setCommissionReports] = useState([]);
  const [schoolWiseReports, setSchoolWiseReports] = useState([]);
  const [commissionFilters, setCommissionFilters] = useState({
    start_date: '',
    end_date: '',
    school_id: '',
    standard: '',
    division: ''
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      // Fetch stats
      const statsResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/agency/dashboard/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const statsData = await statsResponse.json();
      if (statsResponse.ok) {
        setStats(statsData);
      }

      // Fetch schools
      const schoolsResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/agency/schools`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const schoolsData = await schoolsResponse.json();
      if (schoolsResponse.ok) {
        setSchools(schoolsData);
      }

      // Fetch reports
      const reportsResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/agency/reports`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const reportsData = await reportsResponse.json();
      if (reportsResponse.ok) {
        setReports(reportsData);
      }

      // Fetch devices
      const devicesResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/agency/devices`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const devicesData = await devicesResponse.json();
      if (devicesResponse.ok) {
        setDevices(devicesData);
      }

      await loadCommissionData('detail');
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  const loadCommissionData = async (groupBy = 'detail') => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const params = new URLSearchParams();
      params.append('group_by', groupBy);
      if (commissionFilters.start_date) params.append('start_date', commissionFilters.start_date);
      if (commissionFilters.end_date) params.append('end_date', commissionFilters.end_date);
      if (commissionFilters.school_id) params.append('school_id', commissionFilters.school_id);
      if (commissionFilters.standard) params.append('standard', commissionFilters.standard);
      if (commissionFilters.division) params.append('division', commissionFilters.division);

      const summaryResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/agency/commissions/summary?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (summaryResponse.ok) {
        const summaryData = await summaryResponse.json();
        setCommissionSummary(summaryData.summary || {});
      }

      const reportsResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/agency/commissions/reports?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (reportsResponse.ok) {
        const reportsData = await reportsResponse.json();
        setCommissionReports(reportsData.reports || []);
      }

      const schoolParams = new URLSearchParams(params);
      schoolParams.set('group_by', 'school');
      const schoolResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/agency/commissions/reports?${schoolParams}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (schoolResponse.ok) {
        const schoolData = await schoolResponse.json();
        setSchoolWiseReports(schoolData.reports || []);
      }
    } catch (error) {
      console.error('Error loading commission data:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'commission') {
      const groupMap = {
        summary: 'detail',
        date: 'date',
        school: 'school',
        division: 'division',
        event: 'event',
        ledger: 'detail'
      };
      loadCommissionData(groupMap[commissionSubTab] || 'detail');
    }
  }, [activeTab, commissionSubTab]);

  const handleAddSchool = async (e) => {
    e.preventDefault();
    
    // Validate commission
    const defaultCommission = parseFloat(process.env.REACT_APP_SCHOOL_DEFAULT_COMMISSION) || 10;
    const commissionValue = parseFloat(newSchool.sales_commission);
    
    if (commissionValue > defaultCommission) {
      alert(`Commission cannot exceed ${defaultCommission}%`);
      return;
    }
    
    try {
      // API call to add school
      await api.post('/auth/agency/schools', newSchool);
      setShowAddSchool(false);
      setNewSchool({
        name: '',
        email: '',
        phone: '',
        address: '',
        commission_type: 'percentage',
        sales_commission: process.env.REACT_APP_SCHOOL_DEFAULT_COMMISSION || 10
      });
      fetchDashboardData();
    } catch (error) {
      console.error('Error adding school:', error);
      alert(error.message || 'Failed to add school');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: btoa(passwordForm.current_password),
          new_password: btoa(passwordForm.new_password)
        })
      });

      const data = await response.json();

      if (response.ok) {
        alert('Password changed successfully');
        setShowChangePassword(false);
        setPasswordForm({
          current_password: '',
          new_password: ''
        });
      } else {
        alert(data.message || 'Failed to change password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      alert('Network error: ' + error.message);
    }
  };

  const handleSingleAssign = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/agency/devices/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(singleAssignForm)
      });

      const data = await response.json();

      if (response.ok) {
        alert('Device assigned to school successfully');
        setShowSingleAssign(false);
        setSingleAssignForm({ device_id: '', school_id: '' });
        fetchDashboardData();
      } else {
        alert(data.message || 'Failed to assign device');
      }
    } catch (error) {
      console.error('Error assigning device:', error);
      alert('Network error: ' + error.message);
    }
  };

  const handleBulkAssign = async (e) => {
    e.preventDefault();
    
    if (!bulkAssignFile) {
      alert('Please select a file');
      return;
    }

    const formData = new FormData();
    formData.append('file', bulkAssignFile);

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/agency/devices/bulk-assign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        setBulkAssignResult(data);
        alert(`Successfully assigned ${data.successful_count} devices. Failed: ${data.failed_count}`);
        setShowBulkAssign(false);
        setBulkAssignFile(null);
        setBulkAssignResult(null);
        fetchDashboardData();
      } else {
        alert(data.message || 'Failed to bulk assign devices');
      }
    } catch (error) {
      console.error('Error bulk assigning devices:', error);
      alert('Network error: ' + error.message);
    }
  };

  const downloadSampleExcel = () => {
    const sampleData = [
      { 'Device ID': '861261022297317', 'School Name': 'Green Valley Elementary' },
      { 'Device ID': '86126102229731777', 'School Name': 'Sunrise High School' }
    ];
    
    const ws = XLSX.utils.json_to_sheet(sampleData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Device Assignment');
    XLSX.writeFile(wb, 'device_assignment_sample.xlsx');
  };

  const renderOverview = () => (
    <div className="dashboard-overview">
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon schools">🏫</div>
          <div className="stat-content">
            <h3>{stats.totalSchools}</h3>
            <p>Total Schools</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon students">👥</div>
          <div className="stat-content">
            <h3>{stats.activeStudents.toLocaleString()}</h3>
            <p>Active Students</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon devices">📱</div>
          <div className="stat-content">
            <h3>{stats.activeDevices.toLocaleString()}</h3>
            <p>Active Devices</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon revenue">💰</div>
          <div className="stat-content">
            <h3>₹{stats.totalRevenue.toLocaleString()}</h3>
            <p>Total Revenue</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon commission">📈</div>
          <div className="stat-content">
            <h3>₹{stats.commissionEarned.toLocaleString()}</h3>
            <p>Commission Earned</p>
          </div>
        </div>
      </div>
      
      <div className="recent-activity">
        <h3>Recent Activity</h3>
        <div className="activity-list">
          <div className="activity-item">
            <span className="activity-text">New school registered: ABC School</span>
            <span className="activity-time">2 hours ago</span>
          </div>
          <div className="activity-item">
            <span className="activity-text">Subscription renewed: XYZ Academy</span>
            <span className="activity-time">5 hours ago</span>
          </div>
          <div className="activity-item">
            <span className="activity-text">Payment received: ₹45,000</span>
            <span className="activity-time">1 day ago</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSchools = () => (
    <div className="schools-management">
      <div className="section-header">
        <h3>School Management</h3>
        <button className="btn-primary" onClick={() => setShowAddSchool(true)}>
          + Add New School
        </button>
      </div>
      
      {showAddSchool && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h4>Add New School</h4>
            <form onSubmit={handleAddSchool}>
              <div className="form-grid">
                <input
                  type="text"
                  placeholder="School Name"
                  value={newSchool.name}
                  onChange={(e) => setNewSchool({...newSchool, name: e.target.value})}
                  required
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={newSchool.email}
                  onChange={(e) => setNewSchool({...newSchool, email: e.target.value})}
                  required
                />
                <input
                  type="tel"
                  placeholder="Phone"
                  value={newSchool.phone}
                  onChange={(e) => setNewSchool({...newSchool, phone: e.target.value})}
                  required
                />
                <select
                  value={newSchool.commission_type}
                  onChange={(e) => setNewSchool({...newSchool, commission_type: e.target.value})}
                >
                  <option value="percentage">Percentage</option>
                  <option value="fixed">Fixed</option>
                </select>
                <input
                  type="number"
                  placeholder="Sales Commission (%)"
                  value={newSchool.sales_commission}
                  onChange={(e) => setNewSchool({...newSchool, sales_commission: e.target.value})}
                  max={process.env.REACT_APP_SCHOOL_DEFAULT_COMMISSION || 10}
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              <textarea
                placeholder="Address"
                value={newSchool.address}
                onChange={(e) => setNewSchool({...newSchool, address: e.target.value})}
                required
              />
              <div className="modal-actions">
                <button type="button" onClick={() => setShowAddSchool(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Add School</button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      <div className="schools-table">
        <table>
          <thead>
            <tr>
              <th>School Name</th>
              <th>Students</th>
              <th>Status</th>
              <th>Subscription</th>
              <th>Revenue</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {schools.map(school => (
              <tr key={school.id}>
                <td>{school.name}</td>
                <td>{school.students}</td>
                <td><span className="status-badge active">{school.status}</span></td>
                <td>{school.subscription}</td>
                <td>₹{school.revenue.toLocaleString()}</td>
                <td>
                  <button className="btn-action">Edit</button>
                  <button className="btn-action danger">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderCommission = () => {
    const isGrouped = ['date', 'school', 'division', 'event'].includes(commissionSubTab);
    const rows = commissionSubTab === 'school' ? schoolWiseReports : commissionReports;

    return (
      <div className="reports-section">
        <h3>Commission Reports</h3>

        <div className="tab-navigation" style={{ marginBottom: '16px' }}>
          {[
            ['summary', 'Summary'],
            ['date', 'Date-wise'],
            ['school', 'School-wise'],
            ['division', 'Division-wise'],
            ['event', 'Payment Source'],
            ['ledger', 'Detailed Ledger']
          ].map(([key, label]) => (
            <button
              key={key}
              className={`tab-btn ${commissionSubTab === key ? 'active' : ''}`}
              onClick={() => setCommissionSubTab(key)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="report-filters">
          <input
            type="date"
            value={commissionFilters.start_date}
            onChange={(e) => setCommissionFilters({ ...commissionFilters, start_date: e.target.value })}
          />
          <input
            type="date"
            value={commissionFilters.end_date}
            onChange={(e) => setCommissionFilters({ ...commissionFilters, end_date: e.target.value })}
          />
          <select
            className="filter-select"
            value={commissionFilters.school_id}
            onChange={(e) => setCommissionFilters({ ...commissionFilters, school_id: e.target.value })}
          >
            <option value="">All Schools</option>
            {schools.map((school) => (
              <option key={school.id} value={school.id}>{school.name}</option>
            ))}
          </select>
          <input
            placeholder="Standard"
            value={commissionFilters.standard}
            onChange={(e) => setCommissionFilters({ ...commissionFilters, standard: e.target.value })}
          />
          <input
            placeholder="Division"
            value={commissionFilters.division}
            onChange={(e) => setCommissionFilters({ ...commissionFilters, division: e.target.value })}
          />
          <button
            className="btn-primary"
            onClick={() => {
              const groupMap = {
                summary: 'detail',
                date: 'date',
                school: 'school',
                division: 'division',
                event: 'event',
                ledger: 'detail'
              };
              loadCommissionData(groupMap[commissionSubTab] || 'detail');
            }}
          >
            Apply Filters
          </button>
        </div>

        <div className="report-summary">
          <div className="summary-cards">
            <div className="summary-card">
              <h4>Total Commission</h4>
              <p className="summary-value">₹{Number(commissionSummary.total_accrued || 0).toLocaleString()}</p>
            </div>
            <div className="summary-card">
              <h4>Pending</h4>
              <p className="summary-value">₹{Number(commissionSummary.total_pending || 0).toLocaleString()}</p>
            </div>
            <div className="summary-card">
              <h4>Paid</h4>
              <p className="summary-value">₹{Number(commissionSummary.total_paid || 0).toLocaleString()}</p>
            </div>
            <div className="summary-card">
              <h4>Agency Rate</h4>
              <p className="summary-value">{stats.commissionRate || 0}%</p>
            </div>
          </div>
        </div>

        <div className="reports-table">
          <table>
            <thead>
              <tr>
                {isGrouped ? (
                  <>
                    <th>Group</th>
                    <th>Entries</th>
                    <th>Base Amount</th>
                    <th>Commission</th>
                  </>
                ) : (
                  <>
                    <th>Date</th>
                    <th>Event</th>
                    <th>School</th>
                    <th>Device</th>
                    <th>Class/Div</th>
                    <th>Commission</th>
                    <th>Status</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={isGrouped ? 4 : 7}>No commission records yet.</td>
                </tr>
              ) : isGrouped ? (
                rows.map((row, index) => (
                  <tr key={index}>
                    <td>{row.report_label || row.report_key}</td>
                    <td>{row.entry_count}</td>
                    <td>₹{Number(row.total_base_amount || 0).toLocaleString()}</td>
                    <td>₹{Number(row.total_commission || 0).toLocaleString()}</td>
                  </tr>
                ))
              ) : (
                rows.map((row) => (
                  <tr key={row.id}>
                    <td>{new Date(row.created_at).toLocaleDateString()}</td>
                    <td>{row.event_type}</td>
                    <td>{row.school_name || '-'}</td>
                    <td>{row.device_id || '-'}</td>
                    <td>{row.standard || '-'} / {row.division || '-'}</td>
                    <td>₹{Number(row.commission_amount || 0).toLocaleString()}</td>
                    <td>{row.status}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderReports = () => renderCommission();

  const renderDevices = () => (
    <div className="devices-management">
      <div className="section-header">
        <h3>Device Management</h3>
        <div className="header-buttons">
          <button className="btn-primary" onClick={() => setShowSingleAssign(true)}>
            Assign Single Device
          </button>
          <button className="btn-secondary" onClick={() => setShowBulkAssign(true)}>
            Bulk Assign Devices
          </button>
        </div>
      </div>
      
      {showSingleAssign && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h4>Assign Device to School</h4>
            <form onSubmit={handleSingleAssign}>
              <div className="form-grid">
                <input
                  type="text"
                  placeholder="Device ID"
                  value={singleAssignForm.device_id}
                  onChange={(e) => setSingleAssignForm({...singleAssignForm, device_id: e.target.value})}
                  required
                />
                <select
                  value={singleAssignForm.school_id}
                  onChange={(e) => setSingleAssignForm({...singleAssignForm, school_id: e.target.value})}
                  required
                >
                  <option value="">Select School</option>
                  {schools.map(school => (
                    <option key={school.id} value={school.id}>{school.name}</option>
                  ))}
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowSingleAssign(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Assign Device</button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {showBulkAssign && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h4>Bulk Assign Devices to Schools</h4>
            <form onSubmit={handleBulkAssign}>
              <div className="form-group">
                <label>Upload Excel File</label>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setBulkAssignFile(e.target.files[0])}
                  required
                />
              </div>
              <div className="form-group">
                <button type="button" onClick={downloadSampleExcel} className="btn-link">
                  Download Sample Excel
                </button>
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowBulkAssign(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Bulk Assign</button>
              </div>
            </form>
            {bulkAssignResult && (
              <div className="bulk-result">
                <p>Successfully assigned: {bulkAssignResult.successful_count}</p>
                <p>Failed: {bulkAssignResult.failed_count}</p>
                {bulkAssignResult.errors.length > 0 && (
                  <div className="error-list">
                    {bulkAssignResult.errors.map((error, index) => (
                      <p key={index}>Row {error.row}: {error.message}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      
      <div className="devices-table">
        <table>
          <thead>
            <tr>
              <th>Device ID</th>
              <th>Type</th>
              <th>Status</th>
              <th>School</th>
              <th>Battery</th>
              <th>Last Update</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {devices.map(device => (
              <tr key={device.id}>
                <td>{device.device_id}</td>
                <td>{device.type}</td>
                <td><span className={`status-badge ${device.status}`}>{device.status}</span></td>
                <td>{device.school_name || 'Unassigned'}</td>
                <td>{device.battery_level}%</td>
                <td>{new Date(device.last_update).toLocaleString()}</td>
                <td>
                  <button className="btn-action">Unassign</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div className="header-content">
          <div>
            <h1>Agency Dashboard</h1>
            <p>Welcome back, {user?.name || 'Agency Admin'}</p>
          </div>
          <div className="header-actions">
            <button onClick={() => setShowChangePassword(true)} className="btn-secondary">Change Password</button>
            <button onClick={onLogout} className="logout-btn">Logout</button>
          </div>
        </div>
        
        <div className="tab-navigation">
          <button 
            className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button 
            className={`tab-btn ${activeTab === 'schools' ? 'active' : ''}`}
            onClick={() => setActiveTab('schools')}
          >
            Schools
          </button>
          <button 
            className={`tab-btn ${activeTab === 'devices' ? 'active' : ''}`}
            onClick={() => setActiveTab('devices')}
          >
            Devices
          </button>
          <button 
            className={`tab-btn ${activeTab === 'commission' ? 'active' : ''}`}
            onClick={() => setActiveTab('commission')}
          >
            Commission
          </button>
          <button 
            className={`tab-btn ${activeTab === 'reports' ? 'active' : ''}`}
            onClick={() => setActiveTab('reports')}
          >
            Reports
          </button>
        </div>
      </div>
      
      {/* Change Password Modal */}
      {showChangePassword && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h4>Change Password</h4>
            <form onSubmit={handleChangePassword}>
              <div className="form-grid">
                <input
                  type="password"
                  placeholder="Current Password"
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                  required
                />
                <input
                  type="password"
                  placeholder="New Password"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowChangePassword(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Change Password</button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      <div className="dashboard-content">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'schools' && renderSchools()}
        {activeTab === 'devices' && renderDevices()}
        {activeTab === 'commission' && renderCommission()}
        {activeTab === 'reports' && renderReports()}
      </div>
    </div>
  );
};

export default AgencyDashboard;
